import React from 'react';
import { usePharmacyPickerContext } from './PharmacyPickerContext';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { MainTracker } from '../../PixelTrackers/MainTracker';
import { useCustomer } from '../../customHooks/useCustomer';

type LatLng = { lat: number; lng: number };

interface PharmacyMarkersProps {
  allPharmaciesLocations: { [stockCode: string]: { name: string, address: string; location?: LatLng; is_external_pharmacy: boolean; external_integration_code?: string; opening_hours?: string; } };
  selectedStockCode?: string | null;
  highlightedStockCode?: string | null;
  onPharmacySelect?: (stockCode: string | null) => void;
  onPharmacyHighlight?: (stockCode: string | null) => void;
  mapInstanceRef: React.MutableRefObject<any>;
  setSelectedPharmacy: (pharmacy: { name: string, address: string; location?: LatLng; is_external_pharmacy: boolean; opening_hours?: string; } | null) => void;
  setIsModalOpen: (isOpen: boolean) => void;
  setCurrentModalStockCode: (stockCode: string | null) => void;
}

export const PharmacyMarkers: React.FC<PharmacyMarkersProps> = ({
  allPharmaciesLocations,
  selectedStockCode,
  highlightedStockCode,
  onPharmacySelect,
  onPharmacyHighlight,
  mapInstanceRef,
  setSelectedPharmacy,
  setIsModalOpen,
  setCurrentModalStockCode
}) => {
  const { customerState } = useCustomer();
  const { getProductPriceForStock: getPriceForStock } = usePharmacyPickerContext();
  
  const handleMarkerClick = (stockCode: string) => {
    const pharmacy = allPharmaciesLocations[stockCode];
    
    if (pharmacy) {
      const userId = customerState?.customerID || null;
      
      // Get price from selected product's available stocks
      const productsAvailablePrice = getPriceForStock?.(stockCode);
      
      // Track pharmacy card click (from map marker)
      MainTracker.track("custom", "pharmacy_card_click", {
        user_id: userId,
        pharmacy_id: stockCode,
        pharmacy_name: pharmacy.name,
        distance_km: null, // Distance calculation not available in this component
        products_available_Price: productsAvailablePrice,
      });
    }
    
    // Pan to the pharmacy and open modal when clicking a marker
    // Notify parent so carousel can focus this pharmacy; do NOT select yet - user confirms via modal button
    if (onPharmacyHighlight) {
      onPharmacyHighlight(stockCode);
    }
    if (pharmacy?.location && mapInstanceRef.current) {
      mapInstanceRef.current.panTo(pharmacy.location);
      mapInstanceRef.current.setZoom(15);
      setSelectedPharmacy(pharmacy);
      setCurrentModalStockCode(stockCode);
      setIsModalOpen(true);
    }
  }

  const pharmaciesWithLocations = Object.entries(allPharmaciesLocations).filter(([_, stock]) => stock.location);

  return (
    <>
      {pharmaciesWithLocations.map(([stockCode, stock]) => {
        const isSelected = selectedStockCode === stockCode;
        const isHighlighted = highlightedStockCode === stockCode && !isSelected;
        const scale = isSelected ? 1.3 : (isHighlighted ? 1.2 : 1);
        
        // Determine marker icon based on pharmacy type
        let markerIcon = '/unknownStock.png'; // Default: orange (unknown stock)
        
        if (!stock.is_external_pharmacy) {
          // Internal pharmacy (premium) - blue
          markerIcon = '/PremiumPharmacies.png';
        } else if (stock.external_integration_code) {
          // External pharmacy with known stock - green
          markerIcon = '/prescriptionKnowStock.png';
        }
        // else: external pharmacy without integration code - orange (already set as default)
        
        return (
          <AdvancedMarker
            key={stockCode}
            position={stock.location!}
            title={stock.name}
            onClick={() => handleMarkerClick(stockCode)}
          >
            <div
              onClick={(e) => {
                e.stopPropagation();
                handleMarkerClick(stockCode);
              }}
              style={{
                width: `${32 * scale}px`,
                height: `${32 * scale}px`,
                transform: `scale(${scale})`,
                transformOrigin: 'center bottom',
                cursor: 'pointer',
                pointerEvents: 'auto',
              }}
            >
              <img
                src={markerIcon}
                alt="Pharmacy marker"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                }}
              />
            </div>
          </AdvancedMarker>
        );
      })}
    </>
  );
};

